import { DocumentNode } from 'graphql/language';
import gql from 'graphql-tag';

export const USER_FRAGMENT_OPERATOR: DocumentNode = gql`
  fragment userFieldsOperator on user {
    id
    email
    title
    firstName
    lastName
    birthDate
    registrationDate
    mobile1
    mobile2
    fax
    phone
    termsAcceptedOn
    legacy
  }
`;

export const ADDRESS_FRAGMENT_OPERATOR: DocumentNode = gql`
  fragment addressFieldsOperator on address {
    id
    city
    street
    country
    houseNr
    zipCode
    state
    addressSupplement
  }
`;

export const MESSAGE_FRAGMENT_OPERATOR: DocumentNode = gql`
  fragment messageFieldsOperator on message {
    id
    createdOn
    confirmedOn
    subject
    content
    metadata
    needs_confirmation
    fromOperator
  }
`;

export const BANKACCOUNT_FRAGMENT_OPERATOR: DocumentNode = gql`
  fragment bankaccountFieldsOperator on bankAccount {
    id
    accountHolder
    bank
    iban
    bic
  }
`;

export const MOBILESERVICE_FRAGMENT_OPERATOR: DocumentNode = gql`
  fragment mobileserviceFieldsOperator on servicearea_mobile {
    id
    name
    shorthandName
    coordinates
    comment
    confidentialComment
    legacyID
  }
`;

export const OFFICESERVICE_FRAGMENT_OPERATOR: DocumentNode = gql`
  fragment officeserviceFieldsOperator on servicearea_office {
    id
    name
    shorthandName
    comment
    confidentialComment
    legacyID
    address {
      ...addressFieldsOperator
    }
  }
`;

export const DEBTOR_FRAGMENT_OPERATOR: DocumentNode = gql`
  fragment debtorFieldsOperator on debtor {
    id
    debtorNumber
    billingAddress {
      ...addressFieldsOperator
    }
    payment_method {
      ... on debtor_bill_payment_method {
        id
        bankAccount {
          ...bankaccountFieldsOperator
        }
      }
      ... on debtor_sepa_payment_method {
        id
        expirationDate
        bankAccount {
          ...bankaccountFieldsOperator
        }
      }
    }
    tariff {
      id
      period
      validTo
      payment_method {
        ... on debtor_bill_payment_method {
          id
          bankAccount {
            ...bankaccountFieldsOperator
          }
        }
        ... on debtor_sepa_payment_method {
          id
          bankAccount {
            ...bankaccountFieldsOperator
          }
        }
      }
    }
  }
`;

export const SERVICEAREA_FRAGMENT_OPERATOR: DocumentNode = gql`
  fragment serviceareaFieldsOperator on servicearea {
    id
    name
    validFrom
    validTo
    state
    mobileServices {
      id
      name
      shorthandName
      coordinates
      comment
    }
    officeServices {
      id
      name
      shorthandName
      comment
      address {
        ...addressFieldsOperator
      }
    }
  }
`;

export const SERVICE_FRAGMENT_OPERATOR: DocumentNode = gql`
  fragment serviceFieldsOperator on service {
    id
    duplication_of_service_id
    duplicatedBy
    cancelled
    cancellation_reason
    cancellation_fee
    cancelled_on
    createdBy
    createdOn
    card_reader_at
    invoice_number
    start
    end
    kind
    published
    concluded
    legacy
    legacy_bonus_fee
    billNumber
    orderNumber
    stateInsuredPatientCount
    privatePatientCount
    necropsyCount
    consultationViaTelephoneCount
    countsConfirmedOn
    partnerConfirmedContractOn
    substituteConfirmedContractOn
    substituteConfirmedServiceInfoOn
    memo
    comment
    allinclusive
    service_tier
    substitute_fee
    kvShareRateMobile
    kvShareRateOffice
    priority
    discount
    break {
      start
      end
    }
    parentServiceId
    childServices {
      id
    }
    serviceLogistics {
      id
      logisticsStatus
      partnerRequiresFolderDelivery
      partnerRequiresDoctorsBagDelivery
      partnerRequiresCardReader
      cardReaderSerialNumbers
      substituteHasDoctorsBag
    }
    establishmentDoctor {
      ...doctorFieldsOperator
    }
    establishment {
      id
      bsnr
      name
      doctors {
        ...doctorFieldsOperator
      }
      billingAddress {
        ...addressFieldsOperator
      }
      serviceArea {
        ...serviceareaFieldsOperator
      }
      ownedBy {
        id
        bsnr
        name
        type
        verified
        kvSeats
        address {
          ...addressFieldsOperator
        }
        debtor {
          ...debtorFieldsOperator
        }
        establishments {
          id
          name
          bsnr
          billingAddress {
            ...addressFieldsOperator
          }
          doctors {
            ...doctorFieldsOperator
          }
        }
      }
    }
    serviceArea {
      ...serviceareaFieldsOperator
    }
    regularFeeRange {
      min
      max
      current
    }
    holidayFeeRange {
      min
      max
      current
    }
    applicants {
      ...applicantFieldsOperator
    }
    substitute {
      offer
      comment
      details {
        id
        has_doctors_bag
        taxNumber
        bankAccount {
          ...bankaccountFieldsOperator
        }
        user {
          ...userFieldsOperator
        }
        address {
          ...addressFieldsOperator
        }
        debtor {
          ...debtorFieldsOperator
        }
      }
      liabilityInsurance {
        url
      }
      specializations {
        specialization
        certificate {
          url
        }
      }
    }
  }
`;

export const LIST_SERVICE_FRAGMENT_OPERATOR: DocumentNode = gql`
  fragment listServiceFieldsOperator on service {
    id
    createdBy
    start
    end
    applicationsCount
    orderNumber
    allinclusive
    kind
    cancelled
    published
    concluded
    partnerConfirmedContractOn
    substituteConfirmedContractOn
    countsConfirmedOn
    privatePatientCount
    stateInsuredPatientCount
    privatePatientCount
    necropsyCount
    consultationViaTelephoneCount
    kvShareRateMobile
    kvShareRateOffice
    establishmentDoctor {
      name
    }
    establishment {
      name
      ownedBy {
        type
        debtor {
          debtorNumber
        }
      }
    }
    serviceArea {
      name
      state
    }
    regularFeeRange {
      min
      max
      current
    }
    applicants {
      details {
        id
      }
    }
    substitute {
      details {
        debtor {
          debtorNumber
        }
        user {
          title
          firstName
          lastName
          email
          id
        }
      }
    }
  }
`;

export const APPLICANT_FRAGMENT_OPERATOR: DocumentNode = gql`
  fragment applicantFieldsOperator on ServiceSubstitute {
    details {
      id
      taxNumber
      trainingConfirmed
      verified
      memo
      user {
        ...userFieldsOperator
      }
      address {
        ...addressFieldsOperator
      }
      debtor {
        ...debtorFieldsOperator
      }
      assignments {
        ... on mobile_service {
          details {
            id
            start
            end
          }
        }
        ... on office_service {
          details {
            id
            start
            end
          }
        }
      }
    }
    offer
    comment
    isChainCandidate
    liabilityInsurance {
      url
      uploadedOn
    }
    specializations {
      id
      verified
      specialization
      certificate {
        url
        uploadedOn
      }
    }
    source
  }
`;

export const INSTITUTION_FRAGMENT_OPERATOR: DocumentNode = gql`
  fragment institutionFieldsOperator on institution {
    id
    bsnr
    name
    type
    verified
    kvSeats
    address {
      ...addressFieldsOperator
    }
    debtor {
      ...debtorFieldsOperator
    }
    establishments {
      ...establishmentFieldsOperator
    }
  }
`;

export const ESTABLISHMENT_FRAGMENT_OPERATOR: DocumentNode = gql`
  fragment establishmentFieldsOperator on establishment {
    id
    bsnr
    name
    ownedBy {
      type
    }
    doctors {
      ...doctorFieldsOperator
    }
    billingAddress {
      ...addressFieldsOperator
    }
    serviceArea {
      ...serviceareaFieldsOperator
    }
    services {
      ... on mobile_service {
        serviceArea {
          ...mobileserviceFieldsOperator
        }
        details {
          ...serviceFieldsOperator
        }
      }
      ... on office_service {
        serviceArea {
          ...officeserviceFieldsOperator
        }
        details {
          ...serviceFieldsOperator
        }
      }
    }
  }
`;

export const DOCTOR_FRAGMENT_OPERATOR: DocumentNode = gql`
  fragment doctorFieldsOperator on establishment_doctor {
    id
    name
    kvSeats
    lanr
    partner {
      legacy
      registrationDate
      legacyInformation {
        bagHandlingFee
        bonusFee
      }
      shippingAddress {
        id
        city
        street
        country
        houseNr
        zipCode
        state
        addressSupplement
      }
      email
      contactEmail
      invoicingEmail
    }
  }
`;

export const SUBSTITUTE_FRAGMENT_OPERATOR: DocumentNode = gql`
  fragment substituteFieldsOperator on substitute {
    id
    immunityProofVerified
    taxNumber
    trainingConfirmed
    verified
    memo
    user {
      ...userFieldsOperator
    }
    debtor {
      ...debtorFieldsOperator
    }
    bankAccount {
      ...bankaccountFieldsOperator
    }
    applications {
      ... on mobile_service {
        serviceArea {
          ...mobileserviceFieldsOperator
        }
        details {
          ...serviceFieldsOperator
        }
      }
      ... on office_service {
        serviceArea {
          ...officeserviceFieldsOperator
        }
        details {
          ...serviceFieldsOperator
        }
      }
    }
    assignments {
      ... on mobile_service {
        serviceArea {
          ...mobileserviceFieldsOperator
        }
        details {
          ...serviceFieldsOperator
        }
      }
      ... on office_service {
        serviceArea {
          ...officeserviceFieldsOperator
        }
        details {
          ...serviceFieldsOperator
        }
      }
    }
    specializations {
      id
      verified
      specialization
      certificate {
        url
        uploadedOn
      }
    }
    address {
      ...addressFieldsOperator
    }
    license {
      url
      uploadedOn
    }
    identityDocument {
      url
      uploadedOn
    }
    liabilityInsurances {
      id
      insuranceName
      insuranceNumber
      validFrom
      validTo
      document {
        url
        uploadedOn
      }
    }
  }
`;
